import { useState } from "preact/hooks"
import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { ClipboardCheck } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Badge } from "../../../components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Textarea } from "../../../components/ui/textarea"
import { toast } from "../../../components/ui/use-toast"
import { UserState } from "@/auth/user"
import * as ForecastAPI from "../api/forecast_api"
import { is_forecast_admin } from "../utils/version_access"
import { useOrgSettings } from "@/org_settings/state"
import { type ChangeRequestStatus, pending_changes_label } from "../utils/forecast_approvals"

/** SWR key for the pending queue; the save path revalidates it after queueing a change. */
export const PENDING_CHANGE_REQUESTS_KEY = "forecast-change-requests:pending"

interface ForecastApprovalQueueProps {
  className?: string
  /** Called after an approval wrote to a version, so the page can reload the grid. */
  on_applied?: (version_id: string | null) => void | Promise<void>
}

/**
 * The forecast approval queue (GitHub-PR style): a sales rep's save to the primary or a locked
 * version waits here until an org admin approves or rejects it. One shared queue for every admin;
 * a rep sees only their own requests. The header button carries the pending count.
 */
export function ForecastApprovalQueue({ className, on_applied }: ForecastApprovalQueueProps) {
  const user = UserState.use()
  const is_admin = is_forecast_admin(user)
  // The queue only exists in a governed org; elsewhere every save applies directly.
  const governed = useOrgSettings().forecast_approvals_required
  const [open, set_open] = useState(false)
  const [status, set_status] = useState<ChangeRequestStatus>("pending")

  const { data: pending, mutate: mutate_pending } = useSWR(PENDING_CHANGE_REQUESTS_KEY, () =>
    ForecastAPI.listForecastChangeRequests("pending")
  )
  const { data: rows, isLoading, mutate: mutate_rows } = useSWR(
    open ? `forecast-change-requests:${status}` : null,
    () => ForecastAPI.listForecastChangeRequests(status)
  )
  const pending_count = pending?.length ?? 0

  async function refresh(): Promise<void> {
    await Promise.all([mutate_pending(), mutate_rows()])
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={pending_count > 0 ? "default" : "outline"}
        className={`flex items-center gap-2 ${className ?? ""}`}
        title={
          is_admin
            ? "Changes sales reps made to the primary or a locked version, waiting for your approval"
            : "Your changes to the primary or a locked version, waiting for an admin"
        }
        onClick={() => set_open(true)}
      >
        <ClipboardCheck className="h-4 w-4" />
        {pending_count > 0 ? pending_changes_label(user, pending_count) : "Approvals"}
      </Button>
      <Dialog open={open} onOpenChange={set_open}>
        <DialogContent className="max-w-3xl gap-4">
          <DialogHeader>
            <DialogTitle>{is_admin ? "Pending approvals" : "Your changes awaiting approval"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            {is_admin
              ? "Each row is a save a sales rep made to the primary or a locked version. Approving applies it to that version as the rep; rejecting leaves the version untouched."
              : "Saves you made to the primary or a locked version are held here until an org admin approves them. Your grid keeps your edits either way."}
          </p>
          <div className="flex items-center gap-1 rounded-md border border-gray-200 bg-white p-0.5 w-fit">
            {(["pending", "approved", "rejected"] as ChangeRequestStatus[]).map((s: ChangeRequestStatus) => (
              <Button
                key={s}
                type="button"
                size="sm"
                variant={status === s ? "default" : "ghost"}
                className="h-7 px-2 text-xs capitalize"
                onClick={() => set_status(s)}
              >
                {s}
              </Button>
            ))}
          </div>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {isLoading ? <p className="text-sm text-gray-500">Loading…</p> : null}
            {!isLoading && (rows?.length ?? 0) === 0 ? (
              <p className="text-sm text-gray-500">Nothing {status} right now.</p>
            ) : null}
            {(rows ?? []).map((row: ForecastAPI.ForecastChangeRequestRow) => (
              <ChangeRequestCard key={row.id} row={row} can_review={is_admin} on_reviewed={refresh} on_applied={on_applied} />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function format_when(iso: string): string {
  try {
    return format(parseISO(iso), "MMM d, yyyy HH:mm")
  } catch {
    return iso
  }
}

interface ChangeRequestCardProps {
  row: ForecastAPI.ForecastChangeRequestRow
  can_review: boolean
  on_reviewed: () => Promise<void>
  on_applied?: (version_id: string | null) => void | Promise<void>
}

function ChangeRequestCard({ row, can_review, on_reviewed, on_applied }: ChangeRequestCardProps) {
  const [note, set_note] = useState("")
  const [busy, set_busy] = useState<"approve" | "reject" | null>(null)
  const requester = row.requester_name || row.requester_email || "a sales rep"
  const version_deleted = row.version_id == null
  const is_pending = row.status === "pending"

  async function review(action: "approve" | "reject"): Promise<void> {
    set_busy(action)
    try {
      if (action === "approve") {
        await ForecastAPI.approveForecastChangeRequest(row.id, note.trim() || undefined)
        toast({ title: "Change approved", description: `${requester}'s change was applied to ${row.version_name ?? "the version"}.` })
        await on_applied?.(row.version_id)
      } else {
        await ForecastAPI.rejectForecastChangeRequest(row.id, note.trim() || undefined)
        toast({ title: "Change rejected", description: "Nothing was written to the version." })
      }
      await on_reviewed()
    } catch (error) {
      const message = error instanceof Error ? error.message : "The review did not go through"
      toast({ title: action === "approve" ? "Could not approve" : "Could not reject", description: message, variant: "destructive" })
    } finally {
      set_busy(null)
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <p className="text-sm font-medium text-gray-900 break-words">
            {requester} <span className="font-normal text-gray-500">on</span>{" "}
            {row.version_name ?? <span className="italic text-gray-500">a deleted version</span>}
          </p>
          <p className="text-xs text-gray-500">Requested {format_when(row.created_at)}</p>
        </div>
        <Badge
          variant="secondary"
          className={`text-[10px] font-medium capitalize ${
            row.status === "approved" ? "bg-green-100 text-green-800" : row.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
          }`}
        >
          {row.status}
        </Badge>
      </div>
      <p className="text-sm text-gray-700 break-words">{row.summary || "No customer totals moved (structure or settings only)."}</p>
      {!is_pending ? (
        <p className="text-xs text-gray-500">
          {row.status === "approved" ? "Approved" : "Rejected"} by {row.reviewer_email ?? "an admin"}
          {row.reviewed_at ? ` on ${format_when(row.reviewed_at)}` : ""}
          {row.review_note ? ` — ${row.review_note}` : ""}
        </p>
      ) : null}
      {is_pending && can_review ? (
        <div className="space-y-2">
          <Textarea
            value={note}
            onInput={(event: Event) => set_note((event.target as HTMLTextAreaElement).value)}
            placeholder="Note to the requester (optional)"
            rows={2}
            className="text-sm"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              disabled={busy != null || version_deleted}
              title={version_deleted ? "The version this change was made to has been deleted" : undefined}
              onClick={() => void review("approve")}
            >
              {busy === "approve" ? "Applying…" : "Approve"}
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={busy != null} onClick={() => void review("reject")}>
              {busy === "reject" ? "Rejecting…" : "Reject"}
            </Button>
            {version_deleted ? <span className="text-xs text-gray-500">Only reject is possible: the version is gone.</span> : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}
